import { trackButtonClick, trackNutritionAnalysis } from './analytics';
import { v4 as uuidv4 } from 'uuid';

export interface Meal {
  mealId?: string;
  id?: string;
  userId?: string;
  savedAt?: string;
  title?: string;
  name?: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  ingredients: string[];
  timeToPrep?: string;
}

export interface Recipe {
  title: string;
  description: string;
  prepTime: string;
  cookTime: string;
  totalTime: string;
  servings: string;
  ingredients: string[];
  instructions: string[];
  nutritionInfo: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
  tips: string[];
}

export interface RecipeCacheState {
  [key: string]: Recipe;
}

/**
 * Fetch or generate a recipe for a meal
 */
export const fetchRecipe = async (
  meal: Meal,
  savedRecipes: RecipeCacheState,
  setSavedRecipes: (recipes: RecipeCacheState) => void,
  setCurrentRecipe: (recipe: Recipe | null) => void,
  setRecipeLoading: (loading: boolean) => void
): Promise<void> => {
  // Get the meal identifier - could be id or mealId depending on which component called it
  let mealId = meal.mealId || meal.id;

  // If no mealId exists, generate a temporary one based on the meal content
  if (!mealId) {
    // Use title or name, and fallback to a generic name if both are undefined
    const titleStr = meal.title || meal.name || 'unknown_meal';
    mealId = `temp_${titleStr.replace(/\s+/g, '_').toLowerCase()}_${Date.now()}`;
  }

  // Check if we already have the recipe saved in memory
  if (savedRecipes[mealId]) {
    setCurrentRecipe(savedRecipes[mealId]);
    // We don't need to track here since components already track before calling this function
    const mealName = meal.title || meal.name || 'Unknown Meal';
    trackNutritionAnalysis(mealName);
    setRecipeLoading(false);
    return;
  }

  // Start loading
  setRecipeLoading(true);

  try {
    console.log(`Fetching recipe for meal ID: ${mealId}`);

    // Only try to fetch an existing recipe if userId is provided (user is logged in)
    // if (userId) {
    // First try to fetch an existing recipe
    const apiEndpoint = `${process.env.REACT_APP_API_GATEWAY_URL}/prod/getrecipe?mealId=${mealId}`;
    console.log(`Fetching recipe from endpoint: ${apiEndpoint}`);

    const response = await fetch(apiEndpoint);

    if (response.ok) {
      const data = await response.json();
      console.log('Recipe API response:', data);

      if (data && data.recipes && data.recipes.length > 0) {
        // Recipe found in database
        console.log('Recipe found in database:', data.recipes[0]);
        const recipe = data.recipes[0];
        setCurrentRecipe(recipe);

        // Cache the recipe
        setSavedRecipes({ ...savedRecipes, [mealId]: recipe });
        setRecipeLoading(false);
        return;
      }
    } else {
      console.error(`Failed to fetch recipe: ${response.status} ${response.statusText}`);
    }

    await generateRecipe(meal, mealId, savedRecipes, setSavedRecipes, setCurrentRecipe);
  } catch (error) {
    console.error('Error fetching recipe:', error);
    setCurrentRecipe(null);
  } finally {
    setRecipeLoading(false);
  }
};

/**
 * Generate a temporary recipe for a meal - this works with or without login
 */
const generateRecipe = async (
  meal: Meal,
  mealId: string,
  savedRecipes: RecipeCacheState,
  setSavedRecipes: (recipes: RecipeCacheState) => void,
  setCurrentRecipe: (recipe: Recipe | null) => void
): Promise<void> => {
  try {
    const generateEndpoint = `${process.env.REACT_APP_API_GATEWAY_URL}/prod/generaterecipe`;
    console.log(`Generating temporary recipe at endpoint: ${generateEndpoint}`);

    const payload = {
      mealId: mealId,
      title: meal.title || meal.name || 'Unknown Meal',
      ingredients: meal.ingredients,
      nutritionInfo: {
        calories: meal.calories,
        protein: meal.protein,
        carbs: meal.carbs,
        fat: meal.fat
      }
    };

    console.log('Generate recipe payload:', payload);

    const generateResponse = await fetch(generateEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!generateResponse.ok) {
      console.error(`Failed to generate recipe: ${generateResponse.status} ${generateResponse.statusText}`);
      throw new Error('Failed to generate recipe');
    }

    const generatedData = await generateResponse.json();
    console.log('Generated recipe response:', generatedData);

    if (generatedData && generatedData.recipe) {
      console.log('Successfully generated recipe:', generatedData.recipe);
      const recipe = generatedData.recipe;
      setCurrentRecipe(recipe);
      // Save the generated recipe to the database if it's a good one
      try {
        const saveEndpoint = `${process.env.REACT_APP_API_GATEWAY_URL}/prod/saverecipe`;
        console.log(`Saving generated recipe to database at endpoint: ${saveEndpoint}`);
        
        const saveResponse = await fetch(saveEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            mealId: mealId,
            userId: uuidv4(),
            recipe: recipe
          }),
        });
        
        if (!saveResponse.ok) {
          console.warn(`Non-critical: Failed to save generated recipe: ${saveResponse.status} ${saveResponse.statusText}`);
        } else {
          console.log('Successfully saved generated recipe to database');
        }
      } catch (saveError) {
        // This is non-critical, so we just log it and continue
        console.warn('Non-critical: Error saving generated recipe:', saveError);
      }

      // Cache the generated recipe
      setSavedRecipes({ ...savedRecipes, [mealId]: recipe });
    } else {
      console.error('Invalid recipe data received:', generatedData);
      throw new Error('Invalid recipe data received');
    }
  } catch (error) {
    console.error('Error generating recipe:', error);
    setCurrentRecipe(null);
  }
}; 